/**
 * Zimbabwe Country Configuration
 * 
 * This configuration defines Zimbabwe-specific settings for the insurance platform,
 * including administrative levels, payment methods, and agricultural activities.
 */

import { CountryConfig } from './types';

export const zimbabweConfig: CountryConfig = {
  code: 'ZW',
  name: 'Zimbabwe',
  currency: 'USD', // Primary currency for insurance transactions
  
  administrativeLevels: [
    {
      level: 0,
      name: 'Country',
      mandatory: true,
      type: 'predefined', // Always "Zimbabwe"
    },
    {
      level: 1,
      name: 'Province',
      mandatory: true,
      type: 'predefined',
      dataSource: '/api/locations/ZW/levels/1',
    },
    {
      level: 2,
      name: 'District',
      mandatory: true,
      type: 'predefined',
      parentLevel: 1,
      dataSource: '/api/locations/ZW/levels/2/children/:parentId',
    },
    {
      level: 3,
      name: 'Ward',
      mandatory: true,
      type: 'predefined',
      parentLevel: 2,
      dataSource: '/api/locations/ZW/levels/3/children/:parentId',
    },
    {
      level: 4,
      name: 'Village',
      mandatory: false,
      type: 'freetext', // Free text input for village names
    },
  ],
  
  agriculturalActivities: [
    'Maize',
    'Sorghum',
    'Millet',
    'Groundnuts',
    'Sunflower',
    'Soybeans',
    'Cotton',
    'Tobacco',
    'Horticulture (vegetables, fruits)',
    'Dairy production',
    'Poultry',
    'Piggery',
    'Apiculture',
    'Cattle/Goat/Sheep rearing',
    'Fish farming/Aquaculture',
    'Agroforestry',
    'Other',
  ],
  
  paymentMethods: [
    {
      type: 'bank_transfer',
      name: 'Bank Transfer',
      enabled: true,
      fields: [
        {
          name: 'account_name',
          label: 'Account Holder Name',
          required: true,
          type: 'text',
          placeholder: 'Full name as it appears on bank account',
          helpText: 'Must match exactly with bank records',
        },
        {
          name: 'bank_name',
          label: 'Bank Name',
          required: true,
          type: 'select',
          options: [
            'CBZ Bank',
            'Stanbic Bank',
            'Standard Chartered Bank',
            'NMB Bank',
            'ZB Bank',
            'First Capital Bank',
            'Steward Bank',
            'Agribank',
            'Other',
          ],
        },
        {
          name: 'account_number',
          label: 'Account Number',
          required: true,
          type: 'text',
          pattern: '^[0-9]+$',
          placeholder: 'Enter your bank account number',
        },
        {
          name: 'currency',
          label: 'Account Currency',
          required: true,
          type: 'select',
          options: ['USD', 'ZWL'],
        },
        {
          name: 'branch_name_or_code',
          label: 'Branch Name or Code',
          required: false,
          type: 'text',
          placeholder: 'Branch where account is held',
        },
      ],
    },
    {
      type: 'mobile_money',
      name: 'Mobile Money',
      enabled: true,
      fields: [
        {
          name: 'registered_mobile_number',
          label: 'Registered Mobile Number',
          required: true,
          type: 'tel',
          pattern: '^\\+263[0-9]{9}$',
          placeholder: '+263 XX XXX XXXX',
          helpText: 'Include country code (+263). Must be registered with mobile money provider.',
        },
        {
          name: 'full_name',
          label: 'Account Holder Name',
          required: true,
          type: 'text',
          placeholder: 'Name as registered on mobile money account',
          helpText: 'Must match your mobile money account registration',
        },
        {
          name: 'provider',
          label: 'Mobile Money Provider',
          required: false,
          type: 'select',
          options: [
            'EcoCash',
            'OneMoney',
            'Telecash',
          ],
        },
      ],
    },
  ],
  
  documentRequirements: [
    {
      type: 'proof_of_stake',
      name: 'Proof of Stake/Insurable Interest',
      required: true,
      acceptedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 10 * 1024 * 1024, // 10MB
      description: 'Document showing your legal right to insure the land/crop (e.g., land title, lease agreement, chief\'s letter, loan agreement)',
    },
    {
      type: 'national_id',
      name: 'National ID',
      required: false,
      acceptedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 5 * 1024 * 1024, // 5MB
      description: 'Copy of national identification document',
    },
  ],
  
  validationRules: {
    phonePattern: '^\\+263[0-9]{9}$',
    postalCodePattern: '^[0-9]{5}$',
    boundaryBox: {
      minLatitude: -22.4,  // Southernmost point (near South Africa border)
      maxLatitude: -15.6,  // Northernmost point (near Zambia border)
      minLongitude: 25.2,  // Westernmost point (near Botswana border)
      maxLongitude: 33.1,  // Easternmost point (near Mozambique border)
    },
    customRules: {
      mobileMoneyNumber: '^\\+263(71|73|77|78)[0-9]{7}$',
      bankAccountNumber: '^[0-9]{8,12}$',
    },
  },
};